/**
 * **Review** (`/settings/review`, W28, FR-65, plan D86; FR-20, FR-23, FR-36) —
 * which uploads wait for a reviewer, and whether the uploader may approve
 * their own, edited on the page and saved by the bar (today’s `ReviewView`
 * and `ReviewDialog`, the dialog’s fields moved into place):
 *
 * - **Four-eyes approval** — **off by default since W22** (FR-23 refined,
 *   D59): a person who may approve approves their own upload, and its history
 *   says so; an organisation that needs two people turns it on, and one that
 *   saved it keeps what it saved. Read through the package’s `fourEyesOf`, so
 *   its default is the package’s, not a guess here.
 * - **Goes to review on upload** — a tick per type in use (FR-20). A type
 *   switched off keeps what was saved for it, unseen and unchanged.
 *
 * A Save sends one `PATCH` with the version read and `review` alone. Ticking a
 * type and unticking it again is no change. A reader sees every value,
 * disabled, and the bar’s *View only*.
 */
import { SETTINGS, fourEyesOf, type ReviewSettings as ReviewChoice, type SettingsAnswer } from '@fiveox/prodoc-ui'
import { Checkbox, useToast } from '@fiveox/ui'
import { ClipboardCheck, UsersRound } from 'lucide-react'

import { EditBar, EditPage, useLeaveGuard } from '../../components/edit'
import { SettingsSection, SwitchRow } from '../../components/settings-parts'
import { useSettingsHost } from './host'
import { AreaWaiting, SETTINGS_BACK, useSettingsArea } from './settings-data'

/** The page’s slice: the switch in force and each type’s yes or no. */
const reviewOf = (settings: SettingsAnswer): ReviewChoice => ({ defaults: { ...(settings.effective.review?.defaults ?? {}) }, four_eyes: fourEyesOf(settings) })
/** The one key this page sends. */
const reviewPatch = (review: ReviewChoice) => ({ review })

/** The same answer when the switch agrees and every type’s yes or no does — a type absent and one sent `false` are both *no*. */
export function sameReview(a: ReviewChoice, b: ReviewChoice): boolean {
  if (a.four_eyes !== b.four_eyes) return false
  const codes = new Set([...Object.keys(a.defaults), ...Object.keys(b.defaults)])
  return [...codes].every((code) => Boolean(a.defaults[code]) === Boolean(b.defaults[code]))
}

export function ReviewSettings() {
  const { can } = useSettingsHost()
  const { toast } = useToast()
  const area = useSettingsArea({ from: reviewOf, toPatch: reviewPatch, same: sameReview })
  const guard = useLeaveGuard(area.dirty)
  const editable = can(SETTINGS, 'can_update')
  const s = area.settings.data
  const draft = area.draft

  const save = async () => {
    if (await area.save()) toast('Settings saved.')
  }
  const inUse = (s?.effective.document_types ?? []).filter((t) => t.enabled)
  const reviewed = draft ? inUse.filter((t) => draft.defaults[t.code]) : []

  return (
    <EditPage title="Review" description="Which uploads wait for a reviewer, and whether the uploader may approve their own." back={SETTINGS_BACK}>
      {guard}
      {!s || !draft ? (
        <AreaWaiting settings={area.settings} label="Loading the review settings" heights={['h-36', 'h-56']} />
      ) : (
        <>
          <SettingsSection id="four-eyes" icon={<UsersRound size={18} aria-hidden />} title="Four-eyes approval" description="Whether the person who uploaded a version may also approve it.">
            <SwitchRow
              title="Require two people — the uploader of a version cannot approve it"
              srLabel="Four-eyes approval: require two people — the uploader of a version cannot approve it"
              hint={
                <>
                  Off by default: whoever may approve can approve their own upload, and the document’s history says <i>approved their own upload</i>. The
                  reading of a document is reviewed the same way either way.
                </>
              }
              checked={draft.four_eyes}
              disabled={!editable}
              onChange={(four_eyes) => area.setDraft({ ...draft, four_eyes })}
            />
          </SettingsSection>

          <SettingsSection
            id="on-upload"
            icon={<ClipboardCheck size={18} aria-hidden />}
            title="Goes to review on upload"
            description="A new upload of a type ticked here waits for a reviewer. Anything else waits only when someone asks for a review."
            footer={
              <p className="text-muted-foreground text-sm">
                {reviewed.length ? `Goes to review on upload: ${reviewed.map((t) => t.label).join(', ')}.` : 'No type goes to review on upload — a person asks for each review.'}
              </p>
            }
          >
            <fieldset className="grid gap-x-6 gap-y-2 sm:grid-cols-2" aria-label="Types that go to review on upload">
              {inUse.map((t) => (
                <Checkbox
                  key={t.code}
                  label={t.label}
                  checked={Boolean(draft.defaults[t.code])}
                  disabled={!editable}
                  onChange={(e) => area.setDraft({ ...draft, defaults: { ...draft.defaults, [t.code]: e.target.checked } })}
                />
              ))}
            </fieldset>
          </SettingsSection>

          <EditBar dirty={area.dirty} saving={area.saving} problem={area.problem} editable={editable} onSave={() => void save()} onDiscard={area.discard} />
        </>
      )}
    </EditPage>
  )
}
